import { useEffect, useState } from 'react';
import { AUDIT_LABELS, auditWhen, Banner, describeAudit, useApiClient, useQuery, type AuditEntry } from '@acc/web-shared';
import { PageHead, useAgency } from '../agency';

/** The agency's own audit history, for the owner and office manager (baseline 9.15). */
export function Audit() {
  const a = useAgency();
  const api = useApiClient();
  const first = useQuery<{ events: AuditEntry[]; next: number | null }>(a.manager ? a.path('/audit') : null);
  const [older, setOlder] = useState<AuditEntry[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setOlder([]); setNext(first.data?.next ?? null); }, [first.data]);

  async function loadOlder() {
    if (next === null) return;
    setBusy(true); setError('');
    try {
      const r = await api<{ events: AuditEntry[]; next: number | null }>(a.path(`/audit?before=${next}`));
      setOlder((o) => [...o, ...r.events]);
      setNext(r.next);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  if (!a.manager) return <Banner tone="warn">The audit log is for the agency owner and office manager.</Banner>;
  const rows = [...(first.data?.events ?? []), ...older];
  return (
    <>
      <PageHead title="Audit log" sub="Every change in this agency, newest first. Records cannot be edited or removed." />
      <div className="card">
        {(first.error || error) && <Banner tone="bad">{first.error?.message ?? error}</Banner>}
        {first.loading && !first.data && <p className="muted">Loading…</p>}
        {first.data && !rows.length && <div className="empty">No events yet.</div>}
        {rows.length > 0 && (
          <table className="data">
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>Details</th></tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.auditId}>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>{auditWhen(e.occurredAt)}</td>
                  <td>{e.actorName ?? e.actorEmail ?? (e.actorUserId ? 'ACC administration' : <span className="muted">ACC workflow</span>)}</td>
                  <td>{AUDIT_LABELS[e.action] ?? e.action}</td>
                  <td className="muted" style={{ fontSize: 13, overflowWrap: 'anywhere' }}>{describeAudit(e.detail)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {next !== null && <div className="actions"><button type="button" className="btn" onClick={() => void loadOlder()} disabled={busy}>{busy ? 'Loading…' : 'Load older events'}</button></div>}
      </div>
    </>
  );
}
